export const CLIENT_PORTAL_ORG_SLUG = 'client'
export const DEFAULT_PATIENT_PORTAL_ORG_SLUG = 'demo'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1'])
const ORG_SLUG_PATTERN = /^[a-z0-9-]+$/

export function normalizeOrgSlug(slug: string | null | undefined): string | null {
  if (typeof slug !== 'string') {
    return null
  }

  const normalized = slug.trim().toLowerCase()
  if (normalized.length === 0) {
    return null
  }

  return ORG_SLUG_PATTERN.test(normalized) ? normalized : null
}

export function isLocalHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase()
  return (
    LOCAL_HOSTS.has(normalized) ||
    normalized.endsWith('.localhost') ||
    normalized.endsWith('.local')
  )
}

export function getLocalhostOrgSlugFromPath(pathname: string): string | null {
  const pathSegments = pathname.split('/').filter(Boolean)

  if (pathSegments.length >= 2 && pathSegments[1] === 'login') {
    return normalizeOrgSlug(pathSegments[0])
  }

  if (pathSegments[0] === CLIENT_PORTAL_ORG_SLUG) {
    return CLIENT_PORTAL_ORG_SLUG
  }

  return null
}

export function getOrgSlugFromHostname(hostname: string): string | null {
  const hostnameParts = hostname.toLowerCase().split('.')
  const slug = hostnameParts[0] === 'www' ? hostnameParts[1] : hostnameParts[0]
  return normalizeOrgSlug(slug ?? null)
}

export function getTenantSlugFromHostHeader(hostHeader: string | null | undefined): string | null {
  const hostname = normalizeHostHeader(hostHeader)
  if (!hostname) {
    return null
  }

  const parts = hostname.split('.')

  if (parts.length >= 2 && parts.at(-1) === 'localhost') {
    const subdomain = parts[0] === 'www' ? parts[1] : parts[0]
    return normalizeOrgSlug(subdomain ?? null)
  }

  if (parts.length > 2 && parts[0]) {
    const hasWwwPrefix = parts[0] === 'www'
    const effectiveParts = hasWwwPrefix ? parts.slice(1) : parts
    if (effectiveParts.length < 3) {
      return null
    }

    const subdomain = hasWwwPrefix ? parts[1] : parts[0]
    if (subdomain === 'www') {
      return null
    }
    return normalizeOrgSlug(subdomain ?? null)
  }

  return null
}

export function getTenantSlugFromRequestHeaders(args: {
  tenantSlugHeader?: string | null
  host?: string | null
  forwardedHost?: string | null
}): string | null {
  const tenantSlug = normalizeOrgSlug(args.tenantSlugHeader)
  if (tenantSlug) {
    return tenantSlug
  }

  const hostSlug = getTenantSlugFromHostHeader(args.host)
  if (hostSlug) {
    return hostSlug
  }

  const host = normalizeHostHeader(args.host)
  if (host && isLocalHostname(host)) {
    return null
  }

  return getTenantSlugFromHostHeader(args.forwardedHost)
}

function normalizeHostHeader(hostHeader: string | null | undefined): string | null {
  if (typeof hostHeader !== 'string') {
    return null
  }

  const hostname = hostHeader.split(':')[0]?.trim().toLowerCase()
  return hostname || null
}
